import { gql, useQuery } from '@apollo/client';

import type { CloudReachabilityQuery } from '@sbrc/gql/apollo-codegen';

const POLL_INTERVAL_MS = 30_000;

/**
 * `__typename` is a meta-field: it is answered by the GraphQL executor itself
 * and never reaches a resolver, so no authorization rule can turn a healthy
 * box into a false negative. `getRobotById` cannot be used for this —
 * ensureAccessForBot rejects robot-context callers for any robot whose
 * usageMode is CUSTOMER, which is every box in the field.
 */
const CLOUD_REACHABILITY_QUERY = gql`
  query CloudReachability {
    __typename
  }
`;

export interface UseIsCloudConnectedOptions {
  /**
   * Off by default so the probe never runs for callers that haven't decided
   * they need it: an unconditional poll would bill every General-settings
   * session a request every 30 seconds, a 500 and a console error apiece on
   * an offline or unprovisioned box.
   */
  enabled?: boolean;
}

export interface CloudConnectionState {
  /** The box reached api-srv on the most recent check. */
  isCloudConnected: boolean;
  /** No check has resolved yet, so connectivity is still unknown. */
  isChecking: boolean;
}

/**
 * Whether the control box itself can reach the cloud.
 *
 * Remote-control's Apollo client posts to `/api/v1/api-srv/graphql`, a route on
 * botman that signs the request with the robot's JWS identity and forwards it
 * to api-srv — the same path and identity a cloud backup uploads over. So the
 * outcome of any query through it is a statement about the box's connectivity,
 * not the browser's (`navigator.onLine` only describes the tablet).
 *
 * The two outcomes separate cleanly at the proxy. api-srv answering at all —
 * even with GraphQL errors — comes back through jsonController as a 200
 * carrying `{ data, errors }`. Anything that stops the box from reaching it
 * (no route to the internet, api-srv down, no JWS key on an unprovisioned box)
 * throws inside the controller and becomes a 500, which Apollo surfaces as a
 * networkError. Reachability is therefore "we got an answer", not "the answer
 * was a success".
 *
 * Polls only while an enabled consumer is mounted.
 */
export function useIsCloudConnected({
  enabled = false,
}: UseIsCloudConnectedOptions = {}): CloudConnectionState {
  const { data, error, loading } = useQuery<CloudReachabilityQuery>(
    CLOUD_REACHABILITY_QUERY,
    {
      skip: !enabled,
      pollInterval: POLL_INTERVAL_MS,
      // The cache would answer every later poll from the first response and
      // the probe would never notice the box going offline.
      fetchPolicy: 'network-only',
      // Without this a GraphQL-level error discards `data` and reads as
      // offline, even though receiving one proves the box reached api-srv.
      errorPolicy: 'all',
    },
  );

  const reachedCloud =
    data !== undefined || (error?.graphQLErrors?.length ?? 0) > 0;

  return {
    isCloudConnected: reachedCloud && !error?.networkError,
    isChecking: loading && !reachedCloud && !error,
  };
}
